<template>
  <transition name="fade">
    <div class="order-confirmed-overlay">
      <img
        v-if="store && store.logo"
        :src="store.logo"
        class="kiosk-logo-fixed"
      >

      <div class="confirmed-content fade-in">
        <div class="status-card">
          <!-- Success Icon -->
          <div class="success-icon-wrapper">
            <div class="success-circle">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
          </div>

          <h2 class="confirmed-title">
            Order Confirmed!
          </h2>

          <!-- Info Details inside Card -->
          <div class="info-grid">
            <div class="info-group">
              <p class="info-label">
                Pickup Time
              </p>
              <p class="info-value">
                {{ pickupText }}
              </p>
            </div>

            <div class="info-group">
              <p class="info-label">
                Customer
              </p>
              <p class="info-value">
                {{ customerName }}
              </p>
            </div>
          </div>

          <div class="order-id-badge">
            <span class="label">Order #</span>
            <span class="number">{{ orderId }}</span>
          </div>

          <p class="receipt-instruction">
            Please take your receipt.
          </p>
        </div>

        <div class="footer-actions">
          <button
            class="finish-btn"
            @click="handleFinish"
          >
            Finish & Close
          </button>
        </div>

        <!-- Inline Smaller Timer -->
        <div class="inline-countdown">
          <div class="countdown-wrapper">
            <svg
              class="countdown-svg"
              viewBox="0 0 100 100"
            >
              <circle
                class="countdown-circle-bg"
                cx="50"
                cy="50"
                r="45"
              />
              <circle
                class="countdown-circle-progress"
                cx="50"
                cy="50"
                r="45"
                :style="{ strokeDashoffset: strokeDashoffset }"
              />
            </svg>
            <div class="countdown-text">
              <span class="countdown-number">{{ timeLeft }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import moment from "moment";

export default {
  name: "OrderConfirmedOverlay",
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      timeLeft: 30,
      totalTime: 30,
      timer: null,
    };
  },
  computed: {
    store() {
      return this.$store.getters["config/store"];
    },
    orderId() {
      return this.order.id || this.order.local_id || "---";
    },
    customerName() {
      return this.order.customer?.first_name || "Guest";
    },
    pickupText() {
      if (!this.order.serve_at) return "ASAP";
      return moment(this.order.serve_at).format("h:mm A");
    },
    strokeDashoffset() {
      const radius = 45;
      const circumference = 2 * Math.PI * radius;
      return circumference - (this.timeLeft / this.totalTime) * circumference;
    },
  },
  mounted() {
    this.startTimer();
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    startTimer() {
      this.timer = setInterval(() => {
        if (this.timeLeft > 0) {
          this.timeLeft--;
        } else {
          this.handleFinish();
        }
      }, 1000);
    },
    handleFinish() {
      if (this.timer) {
        clearInterval(this.timer);
      }
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.order-confirmed-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #0f172a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* Center main content */
  z-index: 9999;
  color: white;
  overflow: hidden; /* No scroll */
  padding: 2rem;
  text-align: center;
}

.kiosk-logo-fixed {
  max-height: 80px;
  max-width: 240px;
  object-fit: contain;
  position: fixed;
  top: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
}

.confirmed-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem; /* Tighter gap to prevent scroll */
  width: 100%;
  max-width: 400px;
}

.status-card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 32px;
  padding: 3rem 2rem 2.5rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

.success-icon-wrapper {
  margin-bottom: 1rem;
}

.success-circle {
  width: 5rem;
  height: 5rem;
  background-color: #10b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 20px rgba(16, 185, 129, 0.3);
  margin-bottom: 0.5rem;
}

.confirmed-title {
  font-size: 2.25rem;
  font-weight: 800;
  color: white;
  margin: 0;
  letter-spacing: -0.02em;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  border-radius: 20px;
  margin: 0.5rem 0;
}

.info-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  color: #f59e0b;
  font-weight: 700;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0;
}

.info-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: white;
  margin: 0;
}

.order-id-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  font-size: 1rem;
}

.order-id-badge .label {
  color: rgba(255, 255, 255, 0.5);
  font-weight: 600;
}

.order-id-badge .number {
  color: white;
  font-weight: 800;
}

.receipt-instruction {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}

.footer-actions {
  width: 100%;
  display: flex;
  justify-content: center;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Countdown Styles */
.inline-countdown {
  margin-top: 0.5rem;
  display: flex;
  justify-content: center;
}

.countdown-wrapper {
  position: relative;
  width: 60px; /* Smaller size */
  height: 60px;
}

.countdown-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.countdown-circle-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.1);
  stroke-width: 6;
}

.countdown-circle-progress {
  fill: none;
  stroke: #10b981;
  stroke-width: 6;
  stroke-linecap: round;
  stroke-dasharray: 282.7;
  transition: stroke-dashoffset 1s linear;
}

.countdown-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
}

.countdown-number {
  font-size: 1rem;
  font-weight: 800;
  color: white;
}

.finish-btn {
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  border: none;
  padding: 0.5rem 1.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.finish-btn:hover {
  color: white;
  transform: translateY(-2px);
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

.finish-btn:active {
  transform: translateY(0) scale(0.95);
}
</style>
